import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { GetStartedCTA } from "../components/DemoDataControls";
import { getQuotations, getQuotation, createQuotation, updateQuotation, deleteQuotation, duplicateQuotation, convertToSalesOrder, QT_STATUSES } from "../lib/quotations";
import { listCustomers } from "../lib/customers";
import { getProducts } from "../lib/products";
import QuotationForm from "./QuotationForm";
import QuotationView, { QTStatusBadge } from "./QuotationView";
import { todayISO } from "../lib/dates";

const PAGE_SIZE = 25;
const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const isExpired = (q) => q.expiry_date && q.expiry_date < todayISO() && !["accepted", "converted", "rejected"].includes(q.status);

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function QuotationManager({ autoNew, onAutoNew, onOpenSalesOrder }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [converted, setConverted] = useState(null); // { soId, soNumber }

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data, count: total } = await getQuotations({ search, status, customerId, page, pageSize: PAGE_SIZE, sortBy, sortDir }); setRows(data); setCount(total); }
    catch (e) { setError(e.message || "Could not load quotations."); }
    finally { setLoading(false); }
  }, [search, status, customerId, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const loadRefs = useCallback(async () => {
    try { setCustomers(await listCustomers({ status: "active" })); } catch { /* filter degrades */ }
    try { const { data } = await getProducts({ pageSize: 1000, sortBy: "name" }); setProducts(data); } catch { /* picker degrades */ }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onCustomer = (v) => { setCustomerId(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setCustomerId("all"); setPage(1); };

  const openNew = () => setEditing({});
  const openEdit = async (q) => { try { setEditing(await getQuotation(q.id)); } catch (e) { setError(e.message); } };
  const openView = async (q) => { try { setViewing(await getQuotation(q.id)); } catch (e) { setError(e.message); } };

  const save = async (payload) => {
    if (editing && editing.id) await updateQuotation(editing.id, payload);
    else await createQuotation(payload);
    setEditing(null);
    await load();
  };
  const del = async (q) => {
    if (!window.confirm(`Delete ${q.quotation_number}?`)) return;
    try { await deleteQuotation(q.id); await load(); } catch (e) { setError(e.message); }
  };
  const duplicate = async (q) => {
    setActing(true); setError("");
    try { await duplicateQuotation(q.id); setViewing(null); await load(); } catch (e) { setError(e.message); } finally { setActing(false); }
  };
  const convert = async (q) => {
    setActing(true); setError("");
    try {
      const { salesOrderId, salesOrderNumber } = await convertToSalesOrder(q.id);
      setViewing(null);
      setConverted({ soId: salesOrderId, soNumber: salesOrderNumber });
      await load();
    } catch (e) { setError(e.message); } finally { setActing(false); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all" || customerId !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 150 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search quote no., customer, reference…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search quotations" />
        <select style={selStyle} value={customerId} onChange={(e) => onCustomer(e.target.value)} aria-label="Filter by customer">
          <option value="all">All customers</option>
          {customers.map((cu) => <option key={cu.id} value={cu.id}>{cu.company_name}</option>)}
        </select>
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {QT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} quote{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={openNew}>+ New Quotation</button>
      </div>

      {converted && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: C.surface2, border: `1px solid ${C.success}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.success, fontWeight: 700 }}>✅ Converted to Sales Order {converted.soNumber}</span>
          {onOpenSalesOrder && <button style={S.btn()} onClick={() => { onOpenSalesOrder(converted.soId); setConverted(null); }}>Open Sales Order →</button>}
          <button style={S.btn("ghost")} onClick={() => setConverted(null)}>Dismiss</button>
        </div>
      )}

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="Quote Number" col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Customer</th>
              <Th label="Issue Date" col="issue_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Expiry" col="expiry_date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Total" col="total" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={7} style={S.empty}>
                {hasFilters ? (
                  <div>No quotations match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <GetStartedCTA onLoaded={load} fallback={
                    <div>
                      <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No quotations yet</div>
                      <div style={{ marginBottom: 12 }}>Create a quotation, then convert it to a sales order in one click.</div>
                      <button style={S.btn()} onClick={openNew}>+ New Quotation</button>
                    </div>
                  } />
                )}
              </td></tr>
            ) : rows.map((q) => {
              const exp = isExpired(q);
              return (
                <tr key={q.id} style={{ cursor: "pointer" }} onClick={() => openView(q)}>
                  <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{q.quotation_number}</span></td>
                  <td style={S.td}>{q.customer_name || q.customers?.company_name || <span style={{ color: C.textDim }}>—</span>}</td>
                  <td style={S.td}>{q.issue_date || "—"}</td>
                  <td style={S.td}>{q.expiry_date ? <span style={{ color: exp ? C.warning : C.text }}>{exp ? "⚠ " : ""}{q.expiry_date}</span> : "—"}</td>
                  <td style={S.td}><QTStatusBadge status={q.status} /></td>
                  <td style={S.td}>{money(q.total_amount)}</td>
                  <td style={S.td} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button style={S.btn("ghost")} title="View" aria-label={`View ${q.quotation_number}`} onClick={() => openView(q)}>👁</button>
                      {q.status !== "converted" && <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${q.quotation_number}`} onClick={() => openEdit(q)}>✎</button>}
                      <button style={S.btn("ghost")} title="Duplicate" aria-label={`Duplicate ${q.quotation_number}`} onClick={() => duplicate(q)}>⧉</button>
                      {q.status !== "converted" && <button style={S.btn("ghost")} title="Convert to Sales Order" aria-label={`Convert ${q.quotation_number}`} onClick={() => convert(q)}>➡</button>}
                      <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${q.quotation_number}`} onClick={() => del(q)}>🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <QuotationForm initial={editing} customers={customers} products={products} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {viewing && (
        <QuotationView quote={viewing} busy={acting} onClose={() => setViewing(null)} onEdit={(q) => { setViewing(null); openEdit(q); }} onConvert={convert} onDuplicate={duplicate} />
      )}
    </div>
  );
}
